/* RoboFuze — RoboCup events calendar (<rf-events>).
   Data: JSON rendered by sections/rf-events.liquid from `rcj_event` metaobjects.
   Policy: only confirmed dates are ever shown; everything else is "Date TBA". */
(() => {
  if (customElements.get('rf-events')) return;

  const LEVELS = {
    world: { key: 'levelWorld', rank: 0 },
    regional: { key: 'levelRegional', rank: 1 },
    national: { key: 'levelNational', rank: 2 },
    local: { key: 'levelLocal', rank: 3 },
  };
  const DEFAULT_LEVELS = ['world', 'regional', 'national'];

  // Interface text — the single list of defaults. Every key can be overridden in the theme editor through the
  // section's label blocks (sections/rf-events.liquid → data-labels; the block fields show these defaults as
  // placeholders). Placeholders in brackets: [n] number, [country], [place], [month], [season], [name], [date], [q].
  // Keys ending in "One" are the singular form of the key without it (used when [n] is 1).
  const DEFAULT_LABELS = {
    // Block "Labels: figures & filters"
    allCountries: 'All countries',
    reset: 'Reset filters',
    stUpcoming: '[n] upcoming',
    stStarted: '[n] started today',
    stPast: '[n] past',
    stTba: '[n] TBA',
    stProgrammes: '[n] no event yet',
    stStale: '[n] unconfirmed',
    stNone: 'No events match these filters ([n] entries hidden).',
    showLocal: 'Show local qualifiers ([n])',
    hiddenOther: '[n] more hidden',
    hiddenAll: '[n] hidden by filters',
    whyAttending: '[n] not marked “RoboFuze will be there”',
    whyLocal: '[n] local qualifiers',
    whyLocalOne: '[n] local qualifier',
    whyLevels: '[n] at other levels',
    whySoccer: '[n] without RCJ Soccer',
    whyOutside: '[n] outside [place]',
    whyOther: '[n] not matching your other filters',
    // Block "Labels: cards & messages"
    levelWorld: 'World Championship',
    levelRegional: 'Super-regional',
    levelNational: 'National',
    levelLocal: 'Local qualifier',
    soccer: 'RCJ Soccer',
    noSoccer: 'No RCJ Soccer',
    tbc: 'Leagues not confirmed',
    past: 'Past',
    started: 'Started',
    now: 'Happening now',
    website: 'Event website',
    ics: 'Add to calendar',
    source: 'Source',
    noMatch: 'No events match these filters.',
    attendingNone: 'RoboFuze hasn’t confirmed any events yet. We’ll highlight them here as soon as we have.',
    pinned: 'Shown because you followed a link',
    from: 'From',
    endTba: 'end date TBA',
    endUnpublished: 'end date not published',
    dateTba: 'Date TBA',
    tbaShort: 'TBA',
    lastEdition: '(last edition: [date])',
    lastKnown: 'Last known edition: [date].',
    staleLine: 'Current status unconfirmed; latest verified edition: [date].',
    noEventNote: 'No event published yet.',
    hostTba: 'Host country TBA',
    international: 'International',
    websiteShort: 'Website',
    daysN: '[n] days',
    roster: 'RCJ roster (PDF)',
    srNewTab: 'for [name] (opens in a new tab)',
    linked: 'Linked event',
    pinShowLocal: 'Show all local qualifiers',
    pinChange: 'Change the filters to include it',
    pinHide: 'Hide',
    // Block "Labels: list & calendar"
    secPast: '[n] past events this season',
    secPastOne: '[n] past event this season',
    secStarted: 'Started today, end date not announced',
    secStartedIntro: 'These events begin today; the organisers haven’t published when they end. Check the event website for the latest.',
    secTba: 'Dates to be announced',
    secTbaIntro: 'Events held in previous seasons whose next dates are not published yet. We only show dates once organisers confirm them.',
    secProgrammes: 'No event published yet',
    secProgrammesIntro: 'RoboCupJunior programmes with no competition published for the [season] season. We’ll add their events once organisers announce them.',
    seasonCurrent: 'current',
    secStale: 'No recent event information',
    secStaleIntro: 'We have found no edition in the last two years. That doesn’t mean these programmes have stopped; their current status is simply unconfirmed. Tell us if you know more.',
    noneUpcoming: 'No upcoming events with confirmed dates match these filters.',
    countEvent: '[n] events',
    countEventOne: '[n] event',
    countProgramme: '[n] programmes',
    countProgrammeOne: '[n] programme',
    countEntry: '[n] entries',
    countEntryOne: '[n] entry',
    emptyCountry: 'No events or programmes in [country] are in our calendar yet.',
    emptyCountryAsk: 'Know of a RoboCup or RoboCupJunior event there? Tell us at',
    emptyCountryFiltered: 'No [country] entries match these filters.',
    prev: 'Previous month',
    next: 'Next month',
    today: 'Today',
    todayAria: 'today',
    more: '+[n] more',
    calCaption: 'Events in [month]; days with events are buttons that jump to the event details below.',
    calEventsIn: 'Events in [month]',
    calOther: 'Other RoboCup events',
    calTbc: 'Leagues not confirmed yet',
    calUndated: '[n] entries without confirmed dates are in the list view.',
    calUndatedOne: '[n] entry without a confirmed date is in the list view.',
    calShowList: 'Show list view',
    calEmptyMonth: 'No events with confirmed dates in [month] match these filters.',
    // Block "Labels: summary & .ics"
    scopeLead: 'Figures cover:',
    scopeAttending: 'Events RoboFuze attends (all levels and leagues)',
    scopeSoccer: 'RCJ Soccer events (incl. events whose leagues are not confirmed yet)',
    scopeAll: 'All RoboCup events',
    scopeAllLevels: 'all levels incl. local qualifiers',
    scopeWorld: 'World',
    scopeRegional: 'super-regional',
    scopeNational: 'national',
    scopeAnd: 'and',
    scopeNoMain: 'no main levels',
    scopeLocalIn: 'local qualifiers included',
    scopeLocalOut: 'local qualifiers hidden',
    scopeMatching: 'matching “[q]”',
    icsReminder: 'Start-day reminder: only the start date is confirmed; the end date is not published yet.',
    icsStartDay: 'Start day: [name]',
    icsListed: 'Listed on',
    icsSr: '[name] (.ics file)',
    icsSrReminder: '[name] (.ics file, start-day reminder)',
  };
  let LABELS = DEFAULT_LABELS;
  const T = (key, vars = {}) =>
    String(LABELS[key] || DEFAULT_LABELS[key] || '').replace(/\[(\w+)\]/g, (m, v) => (v in vars ? vars[v] : m));
  // Count label: the "…One" key when n is 1.
  const TN = (key, n, vars = {}) => T(n === 1 ? `${key}One` : key, { n, ...vars }) || T(key, { n, ...vars });

  // Month and weekday names from the browser's English (UK) locale data — no hard-coded lists.
  const fmt = (opts) => new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', ...opts });
  const MONTHS = Array.from({ length: 12 }, (_, i) => fmt({ month: 'long' }).format(Date.UTC(2026, i, 1)));
  const MON = Array.from({ length: 12 }, (_, i) => fmt({ month: 'short' }).format(Date.UTC(2026, i, 1)).replace('.', '').slice(0, 3));
  // 2026-01-05 is a Monday: Monday-first week.
  const WEEKDAYS = Array.from({ length: 7 }, (_, i) => fmt({ weekday: 'short' }).format(Date.UTC(2026, 0, 5 + i)));
  const WEEKDAYS_LONG = Array.from({ length: 7 }, (_, i) => fmt({ weekday: 'long' }).format(Date.UTC(2026, 0, 5 + i)));

  const ICON = {
    pin: '<path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z"/><circle cx="12" cy="10" r="2.3"/>',
    external: '<path d="M14 4h6v6M20 4l-9 9M18 14v4.5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10"/>',
    calendarPlus: '<rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M3.5 10h17M8 3v4M16 3v4M12 13v5M9.5 15.5h5"/>',
    ball: '<circle cx="12" cy="12" r="8"/><path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.3 6.3l1.4 1.4M16.3 16.3l1.4 1.4M6.3 17.7l1.4-1.4M16.3 7.7l1.4-1.4"/>',
    check: '<path d="m5 12.5 4.5 4.5L19 7.5"/>',
    chevronL: '<path d="m15 5-7 7 7 7"/>',
    chevronR: '<path d="m9 5 7 7-7 7"/>',
    chevronD: '<path d="m6 9 6 6 6-6"/>',
    globe: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z"/>',
  };
  const icon = (name, cls = '') =>
    `<svg class="rf-icon ${cls}" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${ICON[name]}</svg>`;

  const esc = (v) =>
    String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const fold = (s) =>
    String(s ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const pad = (n) => String(n).padStart(2, '0');
  const iso = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  const parse = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  };
  const addDays = (s, n) => {
    const d = parse(s);
    d.setUTCDate(d.getUTCDate() + n);
    return iso(d);
  };
  const monthKey = (s) => s.slice(0, 7);
  const monthLabel = (key) => `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
  const localToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  // English name of an ISO 3166-1 alpha-2 code, or '' when the code is not a known region.
  const countryName = (cc) => {
    if (!/^[A-Z]{2}$/.test(cc || '')) return '';
    try {
      const name = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' }).of(cc);
      return name && name !== cc ? name : '';
    } catch (e) {
      return '';
    }
  };
  const flag = (cc) =>
    /^[A-Z]{2}$/.test(cc || '') ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '';
  // Months between a "Month YYYY" label (e.g. "March 2018") and today's ISO date; -1 when unparsable.
  const STALE_MONTHS = 24;
  const monthsSince = (label, today) => {
    const m = /^([A-Za-z]+)\s+(\d{4})$/.exec((label || '').trim());
    const mi = m ? MONTHS.indexOf(m[1][0].toUpperCase() + m[1].slice(1).toLowerCase()) : -1;
    if (mi < 0) return -1;
    return (Number(today.slice(0, 4)) - Number(m[2])) * 12 + (Number(today.slice(5, 7)) - 1 - mi);
  };
  // Provenance link. Omitted when the source is the event website, which already has its own button.
  // The official RoboCupJunior representatives roster is a Google Drive PDF: say so instead of a bare "Source".
  const ROSTER_ID = '1aIRAKW0iF_Pxi4grYXEGVand2XSj1oLw';
  const sourceLink = (ev) =>
    ev.source && ev.source !== ev.url
      ? ` <a class="rf-events__src" href="${esc(ev.source)}" target="_blank" rel="noopener">${
          ev.source.includes(ROSTER_ID) ? `${esc(T('source'))}: ${esc(T('roster'))}` : esc(T('source'))
        }<span class="rf-visually-hidden"> ${esc(T('srNewTab', { name: ev.name }))}</span></a>`
      : '';

  function formatRange(start, end, long = false) {
    const a = parse(start);
    const b = parse(end || start);
    const mon = long ? MONTHS : MON;
    const d1 = a.getUTCDate(), m1 = a.getUTCMonth(), y1 = a.getUTCFullYear();
    const d2 = b.getUTCDate(), m2 = b.getUTCMonth(), y2 = b.getUTCFullYear();
    if (start === (end || start)) return `${d1} ${mon[m1]} ${y1}`;
    if (y1 === y2 && m1 === m2) return `${d1}–${d2} ${mon[m1]} ${y1}`;
    if (y1 === y2) return `${d1} ${mon[m1]} – ${d2} ${mon[m2]} ${y1}`;
    return `${d1} ${mon[m1]} ${y1} – ${d2} ${mon[m2]} ${y2}`;
  }

  // ---------- .ics ----------
  const icsText = (s) => String(s ?? '').replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/([,;])/g, '\\$1');
  function icsFold(line) {
    const bytes = new TextEncoder();
    if (bytes.encode(line).length <= 75) return line;
    const out = [];
    let cur = '';
    for (const ch of line) {
      const limit = out.length ? 74 : 75; // continuation lines start with a space
      if (bytes.encode(cur + ch).length > limit) {
        out.push(cur);
        cur = ch;
      } else cur += ch;
    }
    out.push(cur);
    return out.join('\r\n ');
  }
  function buildIcs(ev) {
    const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const location = [ev.venue, ev.city, ev.country].filter(Boolean).join(', ');
    const desc = [
      ev.endKnown ? '' : T('icsReminder'),
      ev.note,
      ev.url ? `${T('website')}: ${ev.url}` : '',
      `${T('icsListed')} ${window.location.origin}/pages/events`,
    ]
      .filter(Boolean)
      .join('\n\n');
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//RoboFuze//RoboCup events//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${ev.handle}@robofuze.com`,
      `DTSTAMP:${stamp}`,
      `DTSTART;VALUE=DATE:${ev.start.replace(/-/g, '')}`,
      // Unknown end: a one-day start-day reminder, never an invented end date.
      `DTEND;VALUE=DATE:${addDays(ev.endKnown ? ev.end : ev.start, 1).replace(/-/g, '')}`,
      `SUMMARY:${icsText(ev.endKnown ? ev.name : T('icsStartDay', { name: ev.name }))}`,
      location ? `LOCATION:${icsText(location)}` : '',
      ev.url ? `URL:${ev.url}` : '',
      `DESCRIPTION:${icsText(desc)}`,
      'TRANSP:TRANSPARENT',
      'END:VEVENT',
      'END:VCALENDAR',
    ].filter(Boolean);
    return lines.map(icsFold).join('\r\n') + '\r\n';
  }
  function downloadIcs(ev) {
    const blob = new Blob([buildIcs(ev)], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ev.handle}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  class RfEvents extends HTMLElement {
    connectedCallback() {
      // Re-attached (e.g. theme editor re-renders): only restore the window listener.
      this._onHash = this._onHash || (() => this.focusHash());
      window.addEventListener('hashchange', this._onHash);
      if (this._ready) return;
      this._ready = true;
      const raw = this.querySelector('[data-rf-events-data]');
      try {
        this.events = JSON.parse(raw ? raw.textContent : '[]');
      } catch (e) {
        this.events = [];
      }
      // Interface labels: owner overrides from the section's label blocks (data-labels) over DEFAULT_LABELS.
      try {
        LABELS = { ...DEFAULT_LABELS, ...JSON.parse(this.dataset.labels || '{}') };
      } catch (e) {
        LABELS = DEFAULT_LABELS;
      }
      // Date basis: the shop date rendered by Liquid (data-today) — the same date the teaser and the Event JSON-LD
      // use. The visitor's clock is only a fallback when the attribute is missing.
      this.today = /^\d{4}-\d{2}-\d{2}$/.test(this.dataset.today || '') ? this.dataset.today : localToday();
      this.pinned = '';
      this.events.forEach((ev) => {
        ev.dated = ev.status === 'confirmed' && !!ev.start;
        ev.endKnown = ev.dated && !!ev.end;
        // `end` is only used to place the event on the grid; an unknown end is shown on its start day.
        if (ev.dated && !ev.end) ev.end = ev.start;
        ev.programme = !ev.dated && ev.status === 'unknown';
        // No edition found for more than two years: shown apart as "No recent event information" (status unconfirmed).
        // We never call an entry inactive without a source saying so.
        ev.stale = !ev.dated && monthsSince(ev.last, this.today) > STALE_MONTHS;
        // Unknown end date: treated as a one-day event for status — "Started" on its start day, past afterwards.
        // No end date is invented: cards still say "end date TBA". Same rule in the Liquid counts, teaser and JSON-LD.
        ev.past = ev.endKnown ? ev.end < this.today : ev.dated && ev.start < this.today;
        ev.now = ev.endKnown && ev.start <= this.today && ev.end >= this.today;
        ev.started = ev.dated && !ev.endKnown && ev.start === this.today;
        ev.soccerRank = ev.soccer === true ? 0 : ev.soccer === false ? 2 : 1;
        ev.search = fold([ev.name, ev.country, ev.cc, ev.city, ev.venue, ev.region, ev.note].join(' '));
      });

      const $ = (sel) => this.querySelector(sel);
      this.ui = {
        output: $('[data-rf-output]'),
        status: $('[data-rf-status]'),
        hint: $('[data-rf-show-local]'),
        search: $('[data-rf-search]'),
        country: $('[data-rf-country]'),
        soccer: $('[data-rf-soccer]'),
        attending: $('[data-rf-attending]'),
        regions: [...this.querySelectorAll('[data-rf-regions] input')],
        levels: [...this.querySelectorAll('[data-rf-level]')],
        views: [...this.querySelectorAll('[data-rf-view]')],
        reset: $('[data-rf-reset]'),
        toggle: $('[data-rf-filters-toggle]'),
        panel: $('[data-rf-filter-panel]'),
        count: $('[data-rf-filter-count]'),
      };
      this.defaults = {
        region: 'all',
        country: '',
        soccer: this.dataset.soccer === 'true',
        attending: false,
        levels: this.dataset.local === 'true' ? [...DEFAULT_LEVELS, 'local'] : [...DEFAULT_LEVELS],
        q: '',
        view: this.dataset.view === 'calendar' ? 'calendar' : 'list',
      };
      this.state = { ...this.defaults, levels: [...this.defaults.levels] };
      this.readUrl();
      this.buildCountries();
      // Country wins over a conflicting region (e.g. ?region=Europe&cc=JP): the region follows the country.
      const sel = this.state.country && this.countries.find(([cc]) => cc === this.state.country);
      if (sel && this.state.region !== 'all' && sel[1].region !== this.state.region) this.state.region = sel[1].region || 'all';
      this.syncControls();
      this.bind();
      this.writeUrl(); // drops invalid parameters (e.g. ?cc=XX) so the URL matches the visible filters

      this.calMonth = monthKey(this.today);
      this.fitCalendarMonth();

      this.render();
      this.focusHash();
    }

    disconnectedCallback() {
      window.removeEventListener('hashchange', this._onHash);
    }

    // ---------- state <-> URL ----------
    readUrl() {
      const p = new URLSearchParams(window.location.search);
      const st = this.state;
      if (p.has('region')) st.region = p.get('region');
      if (p.has('cc')) st.country = p.get('cc').toUpperCase();
      if (p.has('soccer')) st.soccer = p.get('soccer') === '1';
      if (p.has('attending')) st.attending = p.get('attending') === '1';
      if (p.has('levels')) st.levels = p.get('levels').split(',').filter((l) => LEVELS[l]);
      if (p.has('query')) st.q = p.get('query');
      // `view` is reserved by Shopify (alternate templates), so the calendar uses `mode`.
      if (p.get('mode') === 'calendar' || p.get('mode') === 'list') st.view = p.get('mode');
      if (!['all', 'Europe', 'Americas', 'Asia-Pacific', 'Middle East & Africa'].includes(st.region)) st.region = 'all';
    }
    writeUrl() {
      const url = new URL(window.location.href);
      const p = url.searchParams;
      const st = this.state, d = this.defaults;
      const set = (k, v, def) => (v === def ? p.delete(k) : p.set(k, v));
      set('region', st.region, d.region);
      set('cc', st.country, d.country);
      set('soccer', st.soccer ? '1' : '0', d.soccer ? '1' : '0');
      set('attending', st.attending ? '1' : '0', '0');
      set('levels', [...st.levels].sort().join(','), [...d.levels].sort().join(','));
      set('query', st.q.trim(), '');
      set('mode', st.view, d.view);
      if (url.href !== window.location.href) history.replaceState(history.state, '', url);
    }

    buildCountries() {
      const map = new Map();
      this.events.forEach((e) => {
        if (!e.cc) return;
        const c = map.get(e.cc) || { name: e.country || e.cc, region: e.region, n: 0 };
        c.n++;
        map.set(e.cc, c);
      });
      // Extra countries from the section setting ("CZ:Europe, …"): always selectable, even without entries.
      (this.dataset.extraCountries || '').split(',').forEach((item) => {
        const [code, region] = item.split(':').map((s) => s.trim());
        const cc = (code || '').toUpperCase();
        const name = countryName(cc);
        if (name && !map.has(cc)) map.set(cc, { name, region: region || null, n: 0, extra: true });
      });
      // Any other valid ISO country with no entries (e.g. ?cc=BE) stays selected and gets its own empty state.
      const cc = this.state.country;
      if (cc && !map.has(cc)) {
        const name = countryName(cc);
        if (name) map.set(cc, { name, region: null, n: 0 });
        else this.state.country = '';
      }
      this.countries = [...map.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
    }
    renderCountryOptions() {
      const sel = this.ui.country;
      const region = this.state.region;
      const opts = [`<option value="">${esc(T('allCountries'))}</option>`];
      this.countries.forEach(([cc, c]) => {
        const selected = cc === this.state.country;
        if (!selected && region !== 'all' && c.region !== region) return;
        if (c.n === 0 && !c.extra && !selected) return;
        opts.push(`<option value="${cc}">${esc(c.name)}${c.n === 0 ? ' · none yet' : ''}</option>`);
      });
      sel.innerHTML = opts.join('');
      sel.value = this.state.country;
      if (sel.value !== this.state.country) this.state.country = '';
    }

    // Calendar month on load and after every filter change: keep the current month while it has matching dated
    // events, otherwise jump to the next matching month (or the latest past one). Month buttons don't call this.
    fitCalendarMonth() {
      const dated = this.events.filter((e) => e.dated && this.matches(e));
      if (!dated.length) return;
      const first = `${this.calMonth}-01`, last = `${this.calMonth}-31`;
      if (dated.some((e) => e.start <= last && e.end >= first)) return;
      const next = dated.filter((e) => !e.past).sort((a, b) => (a.start < b.start ? -1 : 1))[0];
      if (next) this.calMonth = monthKey(next.start < this.today && (next.now || next.started) ? this.today : next.start);
      else this.calMonth = monthKey(dated.sort((a, b) => (a.end < b.end ? 1 : -1))[0].end);
    }

    syncControls() {
      const st = this.state, ui = this.ui;
      ui.regions.forEach((r) => (r.checked = r.value === st.region));
      this.renderCountryOptions();
      ui.levels.forEach((l) => (l.checked = st.levels.includes(l.value)));
      ui.soccer.checked = st.soccer;
      ui.attending.checked = st.attending;
      ui.search.value = st.q;
      ui.views.forEach((v) => (v.checked = v.value === st.view));
    }

    bind() {
      const ui = this.ui;
      const update = () => {
        this.pinned = ''; // a linked event stays pinned only until the visitor changes the filters
        this.writeUrl();
        this.fitCalendarMonth();
        this.render();
      };
      ui.regions.forEach((r) =>
        r.addEventListener('change', () => {
          this.state.region = r.value;
          // Choosing a region explicitly drops a country from another region (the country select shows the change).
          const c = this.countries.find(([cc]) => cc === this.state.country);
          if (c && r.value !== 'all' && c[1].region !== r.value) this.state.country = '';
          this.renderCountryOptions();
          update();
        }),
      );
      ui.country.addEventListener('change', () => {
        this.state.country = ui.country.value;
        update();
      });
      ui.levels.forEach((l) =>
        l.addEventListener('change', () => {
          this.state.levels = ui.levels.filter((x) => x.checked).map((x) => x.value);
          update();
        }),
      );
      ui.soccer.addEventListener('change', () => {
        this.state.soccer = ui.soccer.checked;
        update();
      });
      ui.attending.addEventListener('change', () => {
        this.state.attending = ui.attending.checked;
        update();
      });
      let t;
      ui.search.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          this.state.q = ui.search.value;
          update();
        }, 160);
      });
      ui.search.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
      });
      ui.views.forEach((v) =>
        v.addEventListener('change', () => {
          this.state.view = v.value;
          update();
        }),
      );
      ui.reset.addEventListener('click', () => {
        this.resetFilters();
        // The sidebar button becomes disabled after a reset: keep focus on the live result summary.
        this.ui.status.focus({ preventScroll: true });
      });
      ui.hint.addEventListener('click', () => {
        if (!this.state.levels.includes('local')) this.state.levels.push('local');
        this.syncControls();
        update();
        // The button hides itself once local qualifiers are shown: keep focus on the live result summary.
        this.ui.status.focus({ preventScroll: true });
      });
      ui.toggle.addEventListener('click', () => {
        const open = ui.toggle.getAttribute('aria-expanded') !== 'true';
        ui.toggle.setAttribute('aria-expanded', String(open));
        this.classList.toggle('is-filters-open', open);
      });

      // Delegated actions inside rendered output
      ui.output.addEventListener('click', (e) => {
        const ics = e.target.closest('[data-rf-ics]');
        if (ics) {
          const ev = this.events.find((x) => x.handle === ics.dataset.rfIcs);
          if (ev && ev.dated) downloadIcs(ev);
          return;
        }
        const nav = e.target.closest('[data-rf-month]');
        if (nav) {
          this.calMonth = nav.dataset.rfMonth;
          this.render();
          const label = this.querySelector('[data-rf-cal-label]');
          const again = this.querySelector(`[data-rf-month-nav="${nav.dataset.rfMonthNav || ''}"]`);
          (again && !again.disabled ? again : label)?.focus();
          return;
        }
        const day = e.target.closest('[data-rf-day]');
        if (day) {
          const target = this.querySelector(`#rf-cal-${CSS.escape(day.dataset.rfDay)}`);
          if (target) {
            target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
            target.focus({ preventScroll: true });
          }
          return;
        }
        if (e.target.closest('[data-rf-reset-inline]')) {
          this.resetFilters();
          // The pressed button is gone after the rebuild: move focus to the (live) result summary.
          this.ui.status.focus({ preventScroll: true });
          return;
        }
        const include = e.target.closest('[data-rf-pin-include]');
        if (include) {
          const ev = this.events.find((x) => x.handle === include.dataset.rfPinInclude);
          if (!ev) return;
          this.state = this.widenFor(ev);
          this.pinned = '';
          this.syncControls();
          this.writeUrl();
          this.fitCalendarMonth();
          this.render();
          this.revealEvent(ev);
          return;
        }
        if (e.target.closest('[data-rf-pin-dismiss]')) {
          this.pinned = '';
          this.render();
          this.ui.status.focus({ preventScroll: true });
        }
      });
    }

    resetFilters() {
      this.pinned = '';
      this.state = { ...this.defaults, levels: [...this.defaults.levels], view: this.state.view };
      this.syncControls();
      this.writeUrl();
      this.fitCalendarMonth();
      this.render();
    }

    // ---------- filtering ----------
    matches(ev, { ignoreLevel = false, ignoreSoccer = false } = {}) {
      const st = this.state;
      if (st.region !== 'all' && ev.region !== st.region && ev.level !== 'world') return false;
      if (st.country && ev.cc !== st.country) return false;
      // "We'll be there" shows every event RoboFuze attends, whatever its level or leagues, so the list always
      // matches the "We'll be at N events" note (region, country and search still apply).
      if (!ignoreLevel && !st.attending && !st.levels.includes(ev.level)) return false;
      if (!ignoreSoccer && st.soccer && !st.attending && ev.soccer === false) return false; // unconfirmed leagues stay visible
      if (st.attending && !ev.attending) return false;
      const q = fold(st.q).trim();
      if (q && !q.split(/\s+/).every((w) => ev.search.includes(w))) return false;
      return true;
    }

    activeFilterCount() {
      const st = this.state, d = this.defaults;
      let n = 0;
      if (st.region !== d.region) n++;
      if (st.country) n++;
      if (st.soccer !== d.soccer) n++;
      if (st.attending) n++;
      if ([...st.levels].sort().join() !== [...d.levels].sort().join()) n++;
      return n;
    }

    // ---------- rendering ----------
    render() {
      const list = this.events.filter((e) => this.matches(e));
      const dated = list.filter((e) => e.dated);
      const stale = list.filter((e) => e.stale);
      const tba = list.filter((e) => !e.dated && !e.programme && !e.stale);
      const programmes = list.filter((e) => e.programme && !e.stale);
      this.current = { list, dated, tba, programmes, stale };

      const n = this.activeFilterCount() + (this.state.q.trim() ? 1 : 0);
      this.ui.count.hidden = n === 0;
      this.ui.count.textContent = n ? String(n) : '';
      this.ui.reset.disabled = n === 0;
      this.classList.toggle('is-filtered', n > 0);

      // One rule everywhere (list, calendar, counts, teaser, JSON-LD): an event with an unannounced end date is
      // treated as a one-day event for status — upcoming before its start day, "started" on it, past afterwards.
      const startedN = dated.filter((e) => e.started).length;
      const pastN = dated.filter((e) => e.past).length;
      const upcoming = dated.length - startedN - pastN;
      const hidden = this.events.length - list.length;
      // Countries of the selected region only: the World Championship is listed under every region,
      // but its host country is not part of that region (and not in its country list).
      const countries = new Set(list.filter((e) => this.state.region === 'all' || e.region === this.state.region).map((e) => e.cc).filter(Boolean)).size;

      // One definition of "shown": the results line lists every shown entry by category (the same categories as
      // the figures and the list sections). Entries not shown are either local qualifiers (offered by the single
      // "Show local qualifiers (N)" button) or hidden by other filters (the disclosure). shown + local + other = total.
      const hiddenLocal = this.state.levels.includes('local') || this.state.attending
        ? 0
        : this.events.filter((e) => e.level === 'local' && this.matches(e, { ignoreLevel: true })).length;
      const item = (key, n, strong = false) => {
        const txt = esc(T(key, { n }));
        return `<span class="rf-events__stitem">${strong ? `<strong>${txt}</strong>` : txt}</span>`;
      };
      const parts = [item('stUpcoming', upcoming, true)];
      if (startedN) parts.push(item('stStarted', startedN));
      if (pastN) parts.push(item('stPast', pastN));
      if (tba.length) parts.push(item('stTba', tba.length));
      if (programmes.length) parts.push(item('stProgrammes', programmes.length));
      if (stale.length) parts.push(item('stStale', stale.length));
      this.ui.status.innerHTML = list.length
        ? parts.join(', ') // commas, not "·": a separator must never start a wrapped line
        : esc(T('stNone', { n: this.events.length }));

      this.ui.hint.hidden = hiddenLocal === 0;
      this.ui.hint.textContent = hiddenLocal ? T('showLocal', { n: hiddenLocal }) : '';
      const otherHidden = hidden - hiddenLocal;
      const hb = this.querySelector('[data-rf-hidden-box]');
      if (hb) {
        hb.hidden = !otherHidden || !list.length;
        hb.querySelector('[data-rf-hidden-label]').textContent = T(hiddenLocal ? 'hiddenOther' : 'hiddenAll', { n: otherHidden });
        hb.querySelector('[data-rf-hidden-text]').textContent = otherHidden ? `${this.hiddenSummary(hiddenLocal > 0)}.` : '';
      }

      const datedSub = [T('stUpcoming', { n: upcoming })];
      if (startedN) datedSub.push(T('stStarted', { n: startedN }));
      if (pastN) datedSub.push(T('stPast', { n: pastN }));
      // Confirmed dates + dates TBA + no event yet + status unconfirmed = the entries shown.
      this.updateStats({ dated: dated.length, datedsub: datedSub.join(', '), tba: tba.length, programmes: programmes.length, stale: stale.length, countries });
      const staleBox = (this.closest('.rf-events') || document).querySelector('[data-rf-stat-box="stale"]');
      if (staleBox) staleBox.hidden = stale.length === 0;

      this.ui.output.innerHTML = this.state.view === 'calendar' ? this.renderCalendar() : this.renderList();
      this.dataset.activeView = this.state.view;
    }

    // Why entries are hidden, each counted once in this order: attending, level, RCJ Soccer, everything else.
    // skipLocal: local qualifiers are already offered by the "Show local qualifiers (N)" button.
    hiddenSummary(skipLocal = false) {
      const st = this.state;
      let local = 0, otherLevel = 0, noSoccer = 0, rest = 0;
      let notAttending = 0;
      this.events.forEach((e) => {
        if (this.matches(e)) return;
        // "We'll be there" on: level and leagues don't apply, so everything else is hidden for that reason.
        if (st.attending && !e.attending) notAttending++;
        // Hidden only by the level filter: same rule as the "Show local qualifiers" button.
        else if (this.matches(e, { ignoreLevel: true })) e.level === 'local' ? local++ : otherLevel++;
        // Without RCJ Soccer: only entries that the soccer filter alone (plus level) hides — not other countries.
        else if (this.matches(e, { ignoreLevel: true, ignoreSoccer: true })) noSoccer++;
        else rest++;
      });
      const out = [];
      if (notAttending) out.push(T('whyAttending', { n: notAttending }));
      if (local && !skipLocal) out.push(T(local === 1 ? 'whyLocalOne' : 'whyLocal', { n: local }));
      if (otherLevel) out.push(T('whyLevels', { n: otherLevel }));
      if (noSoccer) out.push(T('whySoccer', { n: noSoccer }));
      if (rest) {
        // Only the country (or region) is narrowing the list: name it instead of "your other filters".
        const c = st.country && this.countries.find(([cc]) => cc === st.country);
        if (!st.q.trim() && c) out.push(T('whyOutside', { n: rest, place: c[1].name }));
        else if (!st.q.trim() && !st.country && st.region !== 'all') out.push(T('whyOutside', { n: rest, place: st.region }));
        else out.push(T('whyOther', { n: rest }));
      }
      return out.join(', ');
    }

    // Headline figures always describe exactly what the list shows.
    updateStats(counts) {
      const root = this.closest('.rf-events') || document;
      Object.entries(counts).forEach(([k, v]) => {
        const el = root.querySelector(`[data-rf-stat="${k}"]`);
        if (el) el.textContent = v;
      });
      const scope = root.querySelector('[data-rf-scope]');
      if (!scope) return;
      const st = this.state;
      const parts = [];
      if (st.attending) parts.push(T('scopeAttending'));
      else {
        parts.push(st.soccer ? T('scopeSoccer') : T('scopeAll'));
        const lv = ['world', 'regional', 'national'].filter((l) => st.levels.includes(l));
        const lvNames = { world: T('scopeWorld'), regional: T('scopeRegional'), national: T('scopeNational') };
        if (lv.length === 3 && st.levels.includes('local')) parts.push(T('scopeAllLevels'));
        else {
          parts.push(lv.length ? lv.map((l) => lvNames[l]).join(', ').replace(/, ([^,]*)$/, ` ${T('scopeAnd')} $1`) : T('scopeNoMain'));
          parts.push(st.levels.includes('local') ? T('scopeLocalIn') : T('scopeLocalOut'));
        }
      }
      if (st.country) {
        const c = this.countries.find(([cc]) => cc === st.country);
        parts.push(c ? c[1].name : st.country);
      } else if (st.region !== 'all') parts.push(st.region);
      if (st.q.trim()) parts.push(T('scopeMatching', { q: st.q.trim() }));
      scope.textContent = parts.join('; ');
    }

    emptyState() {
      const st = this.state;
      const attendingOnly = st.attending && !this.events.some((e) => e.attending);
      const c = st.country ? this.countries.find(([cc]) => cc === st.country) : null;
      const email = this.dataset.email || 'info@robofuze.com';
      let msg = T('noMatch');
      let extra = '';
      if (attendingOnly) msg = T('attendingNone');
      else if (c && c[1].n === 0) {
        msg = T('emptyCountry', { country: c[1].name });
        extra = `<p>${esc(T('emptyCountryAsk'))} <a class="rf-link" href="mailto:${esc(email)}">${esc(email)}</a>.</p>`;
      } else if (c) msg = T('emptyCountryFiltered', { country: c[1].name });
      return `<div class="rf-events__empty">
        ${icon('globe')}
        <p>${esc(msg)}</p>
        ${extra}
        <button type="button" class="rf-btn rf-btn--ghost rf-btn--sm" data-rf-reset-inline>${esc(T('reset'))}</button>
      </div>`;
    }

    renderList() {
      const { list, dated, tba, programmes, stale } = this.current;
      const pinNow = this.pinned ? this.events.find((e) => e.handle === this.pinned && !this.matches(e)) : null;
      if (!list.length) return (pinNow ? this.pinnedBlock(pinNow) : '') + this.emptyState();
      // Same day: RCJ Soccer first, then unconfirmed leagues, then other RoboCup events; then by level.
      const sameDay = (a, b) => a.soccerRank - b.soccerRank || LEVELS[a.level].rank - LEVELS[b.level].rank || a.name.localeCompare(b.name);
      const byDate = (a, b) => (a.start === b.start ? sameDay(a, b) : a.start < b.start ? -1 : 1);
      const upcoming = dated.filter((e) => !e.past && !e.started).sort(byDate);
      const started = dated.filter((e) => e.started).sort(byDate);
      const past = dated.filter((e) => e.past).sort((a, b) => (a.start === b.start ? sameDay(a, b) : a.start < b.start ? 1 : -1));
      const html = [];

      // A linked event (#rf-event-…) that the current filters exclude: shown first, on its own, without widening
      // the filters for everything else.
      if (pinNow) html.push(this.pinnedBlock(pinNow));

      if (past.length) {
        html.push(`<details class="rf-events__past">
          <summary><h2 class="rf-events__sum-title">${esc(TN('secPast', past.length))}</h2>${icon('chevronD', 'rf-events__chev')}</summary>
          <div class="rf-events__cards">${past.map((e) => this.card(e, 'event')).join('')}</div>
        </details>`);
      }

      if (started.length) {
        html.push(`<section class="rf-events__month rf-events__started" aria-label="${esc(T('secStarted'))}">
          <h2 class="rf-events__month-title">${esc(T('secStarted'))} <span>${esc(TN('countEvent', started.length))}</span></h2>
          <p class="rf-events__tba-intro">${esc(T('secStartedIntro'))}</p>
          <div class="rf-events__cards">${started.map((e) => this.card(e, 'event')).join('')}</div>
        </section>`);
      }

      if (upcoming.length) {
        const groups = new Map();
        upcoming.forEach((e) => {
          const key = e.now ? monthKey(this.today) : monthKey(e.start);
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(e);
        });
        for (const [key, evs] of groups) {
          html.push(`<section class="rf-events__month" aria-label="${esc(monthLabel(key))}">
            <h2 class="rf-events__month-title">${esc(monthLabel(key))} <span>${esc(TN('countEvent', evs.length))}</span></h2>
            <div class="rf-events__cards">${evs.map((e) => this.card(e, 'event')).join('')}</div>
          </section>`);
        }
      } else {
        html.push(`<p class="rf-events__none">${esc(T('noneUpcoming'))}</p>`);
      }

      if (tba.length) {
        const sorted = [...tba].sort(
          (a, b) =>
            LEVELS[a.level].rank - LEVELS[b.level].rank ||
            a.soccerRank - b.soccerRank ||
            (a.country || '').localeCompare(b.country || '') ||
            a.name.localeCompare(b.name),
        );
        html.push(`<section class="rf-events__month rf-events__tba" aria-label="${esc(T('secTba'))}">
          <h2 class="rf-events__month-title">${esc(T('secTba'))} <span>${esc(TN('countEvent', tba.length))}</span></h2>
          <p class="rf-events__tba-intro">${esc(T('secTbaIntro'))}</p>
          <div class="rf-events__cards rf-events__cards--tba">${sorted.map((e) => this.card(e, 'event', true)).join('')}</div>
        </section>`);
      }

      if (programmes.length) {
        const sorted = [...programmes].sort(
          (a, b) => (a.country || '').localeCompare(b.country || '') || a.name.localeCompare(b.name),
        );
        html.push(`<details class="rf-events__past rf-events__pgroup">
          <summary><h2 class="rf-events__sum-title">${esc(T('secProgrammes'))} (${esc(TN('countProgramme', sorted.length))})</h2>${icon('chevronD', 'rf-events__chev')}</summary>
          <div class="rf-events__pgroup-body">
            <p class="rf-events__tba-intro">${esc(T('secProgrammesIntro', { season: this.dataset.season || T('seasonCurrent') }))}</p>
            <ul class="rf-events__proglist" role="list">${sorted.map((e) => this.programmeRow(e)).join('')}</ul>
          </div>
        </details>`);
      }

      if (stale.length) {
        const sorted = [...stale].sort((a, b) => (a.country || '').localeCompare(b.country || '') || a.name.localeCompare(b.name));
        html.push(`<details class="rf-events__past rf-events__pgroup rf-events__stale">
          <summary><h2 class="rf-events__sum-title">${esc(T('secStale'))} (${esc(TN('countEntry', sorted.length))})</h2>${icon('chevronD', 'rf-events__chev')}</summary>
          <div class="rf-events__pgroup-body">
            <p class="rf-events__tba-intro">${esc(T('secStaleIntro'))}</p>
            <ul class="rf-events__proglist" role="list">${sorted.map((e) => this.programmeRow(e)).join('')}</ul>
          </div>
        </details>`);
      }
      return html.join('');
    }

    // The smallest change to the current filters that includes `ev` (keeps everything that already fits it).
    widenFor(ev) {
      const st = this.state;
      const s = { ...st, levels: [...st.levels], attending: st.attending && ev.attending };
      if (!s.levels.includes(ev.level)) s.levels.push(ev.level);
      if (s.soccer && ev.soccer === false) s.soccer = false;
      if (s.country && ev.cc !== s.country) s.country = '';
      if (s.region !== 'all' && ev.region !== s.region && ev.level !== 'world') s.region = 'all';
      const q = fold(s.q).trim();
      if (q && !q.split(/\s+/).every((w) => ev.search.includes(w))) s.q = '';
      return s;
    }

    pinnedBlock(ev) {
      const st = this.state;
      const w = this.widenFor(ev);
      const onlyLocal =
        ev.level === 'local' && !st.levels.includes('local') &&
        w.soccer === st.soccer && w.country === st.country && w.region === st.region && w.q === st.q && w.attending === st.attending;
      const label = onlyLocal ? T('pinShowLocal') : T('pinChange');
      const body = ev.dated
        ? `<div class="rf-events__cards">${this.card(ev, 'event')}</div>`
        : ev.programme || ev.stale
          ? `<ul class="rf-events__proglist" role="list">${this.programmeRow(ev)}</ul>`
          : `<div class="rf-events__cards">${this.card(ev, 'event', true)}</div>`;
      return `<section class="rf-events__pinned" aria-labelledby="rf-pin-${esc(ev.handle)}">
        <h2 class="rf-visually-hidden" id="rf-pin-${esc(ev.handle)}">${esc(T('linked'))}</h2>
        <p class="rf-events__pin-note">
          <span>${esc(T('pinned'))}.</span>
          <button type="button" class="rf-events__hint" data-rf-pin-include="${esc(ev.handle)}">${esc(label)}</button>
          <button type="button" class="rf-events__hint rf-events__hint--quiet" data-rf-pin-dismiss>${esc(T('pinHide'))}</button>
        </p>
        ${body}
      </section>`;
    }

    revealEvent(ev) {
      const el = this.querySelector(`#rf-event-${CSS.escape(ev.handle)}`);
      if (!el) return;
      const det = el.closest('details');
      if (det) det.open = true;
      el.classList.add('is-target');
      requestAnimationFrame(() => {
        el.scrollIntoView({ block: 'center' });
        el.focus({ preventScroll: true });
      });
    }

    programmeRow(ev) {
      const id = `rf-event-${ev.handle}`;
      const where = ev.country
        ? `<span class="rf-flag" aria-hidden="true">${flag(ev.cc)}</span><span class="rf-events__progcountry">${esc(ev.country)}</span>`
        : `${icon('globe')}<span class="rf-events__progcountry">${esc(T('international'))}</span>`;
      const level = ev.level === 'local' ? `<span class="rf-events__level rf-events__level--local">${esc(T((LEVELS['local'] || LEVELS.national).key))}</span>` : '';
      return `<li class="rf-events__prog-item" id="${esc(id)}" tabindex="-1">
        <div class="rf-events__prog-main">
          <p class="rf-events__prog-where">${where}</p>
          <h3 class="rf-events__prog-name">${esc(ev.name)} ${level}</h3>
          <p class="rf-events__prog-note">${esc(ev.note || T('noEventNote'))}${
            ev.last ? ` ${esc(T(ev.stale ? 'staleLine' : 'lastKnown', { date: ev.last }))}` : ''
          }${sourceLink(ev)}</p>
        </div>
        ${
          ev.url
            ? `<a class="rf-events__action" href="${esc(ev.url)}" target="_blank" rel="noopener">${esc(T('websiteShort'))}${icon('external')}<span class="rf-visually-hidden"> ${esc(T('srNewTab', { name: ev.name }))}</span></a>`
            : ''
        }
      </li>`;
    }

    card(ev, prefix, compact = false) {
      const attendingLabel = this.dataset.attendingLabel || 'RoboFuze will be there';
      const badges = [];
      badges.push(`<span class="rf-events__level rf-events__level--${esc(ev.level)}">${esc(T((LEVELS[ev.level] || LEVELS.national).key))}</span>`);
      if (ev.soccer === true) badges.push(`<span class="rf-events__tag rf-events__tag--soccer">${icon('ball')}${esc(T('soccer'))}</span>`);
      else if (ev.soccer === false) badges.push(`<span class="rf-events__tag rf-events__tag--nosoccer">${esc(T('noSoccer'))}</span>`);
      else badges.push(`<span class="rf-events__tag rf-events__tag--tbc">${esc(T('tbc'))}</span>`);
      if (ev.past) badges.push(`<span class="rf-events__tag rf-events__tag--past">${esc(T('past'))}</span>`);
      if (ev.started) badges.push(`<span class="rf-events__tag rf-events__tag--past">${esc(T('started'))}</span>`);
      if (ev.now) badges.push(`<span class="rf-events__tag rf-events__tag--now">${esc(T('now'))}</span>`);
      if (ev.attending) badges.push(`<span class="rf-events__tag rf-events__tag--attending">${icon('check')}${esc(attendingLabel)}</span>`);

      // "Macao · Macao": a city equal to the country name adds nothing.
      const city = ev.city && fold(ev.city) !== fold(ev.country) ? ev.city : '';
      // Address order, comma-separated (no "·" that could start or end a wrapped line).
      const where = [ev.venue, city].filter(Boolean).map(esc).join(', ');
      const country = ev.country
        ? `<span class="rf-events__country"><span class="rf-flag" aria-hidden="true">${flag(ev.cc)}</span>${esc(ev.country)}</span>`
        : `<span class="rf-events__country">${icon('globe')}${esc(T(ev.level === 'regional' ? 'hostTba' : 'international'))}</span>`;

      let dateBlock, when;
      if (ev.dated) {
        const a = parse(ev.start), b = parse(ev.end);
        const sameMonth = ev.start.slice(0, 7) === ev.end.slice(0, 7);
        const dayTxt = ev.start === ev.end ? a.getUTCDate() : sameMonth ? `${a.getUTCDate()}–${b.getUTCDate()}` : a.getUTCDate();
        const sub = !sameMonth ? `<span class="rf-events__dsub">–${b.getUTCDate()} ${MON[b.getUTCMonth()]}</span>` : `<span class="rf-events__dsub">${a.getUTCFullYear()}</span>`;
        dateBlock = `<div class="rf-events__date" aria-hidden="true"><span class="rf-events__dmon">${MON[a.getUTCMonth()]}</span><span class="rf-events__dday">${dayTxt}</span>${sub}</div>`;
        const span = Math.round((b - a) / 864e5);
        const wdA = (a.getUTCDay() + 6) % 7, wdB = (b.getUTCDay() + 6) % 7;
        const wd = span === 0 ? WEEKDAYS_LONG[wdA] : span < 7 ? `${WEEKDAYS[wdA]}–${WEEKDAYS[wdB]}` : esc(T('daysN', { n: span + 1 }));
        // No "·" separators here: a separator can end up at the start or end of a wrapped line. Parentheses and
        // commas wrap naturally ("From 16 April 2027 (Friday), end date TBA").
        if (ev.endKnown) {
          when = `<p class="rf-events__when"><time datetime="${ev.start}">${esc(formatRange(ev.start, ev.end, true))}</time> <span class="rf-events__wd">(${wd})</span></p>`;
        } else {
          // After the start day an open-ended event counts as past; it never gets an invented end date.
          const begun = ev.started || ev.past;
          const lead = esc(T(begun ? 'started' : 'from'));
          const wdTxt = begun ? '' : ` <span class="rf-events__wd">(${WEEKDAYS_LONG[wdA]})</span>`;
          when = `<p class="rf-events__when">${lead} <time datetime="${ev.start}">${esc(formatRange(ev.start, ev.start, true))}</time>${wdTxt}, <span class="rf-events__nowrap">${esc(T(ev.past ? 'endUnpublished' : 'endTba'))}</span></p>`;
        }
      } else {
        dateBlock = `<div class="rf-events__date rf-events__date--tba" aria-hidden="true"><span class="rf-events__dday">${esc(T('tbaShort'))}</span></div>`;
        when = `<p class="rf-events__when rf-events__when--tba">${esc(T('dateTba')).replace(/ /g, '\u00a0')}${ev.last ? ` <span class="rf-events__nowrap">${esc(T('lastEdition', { date: ev.last }))}</span>` : ''}</p>`;
      }

      const actions = [];
      if (ev.url) {
        actions.push(`<a class="rf-events__action rf-events__action--primary" href="${esc(ev.url)}" target="_blank" rel="noopener">
          ${esc(T('website'))}${icon('external')}<span class="rf-visually-hidden"> ${esc(T('srNewTab', { name: ev.name }))}</span></a>`);
      }
      // Only for events with a confirmed start day, offered until the event has started (an unknown end date makes
      // it a start-day reminder) or, with a known end, until it is over.
      if (ev.dated && !ev.past && !ev.started) {
        actions.push(`<button type="button" class="rf-events__action" data-rf-ics="${esc(ev.handle)}">
          ${icon('calendarPlus')}${esc(T('ics'))}<span class="rf-visually-hidden">: ${esc(T(ev.endKnown ? 'icsSr' : 'icsSrReminder', { name: ev.name }))}</span></button>`);
      }
      const source = sourceLink(ev).trim();

      const id = `rf-${prefix === 'cal' ? 'cal' : 'event'}-${ev.handle}`;
      const cls = ['rf-events__card'];
      if (ev.attending) cls.push('is-attending');
      if (ev.past) cls.push('is-past');
      if (compact) cls.push('rf-events__card--compact');
      return `<article class="${cls.join(' ')}" id="${esc(id)}" tabindex="-1" aria-labelledby="${esc(id)}-t">
        ${dateBlock}
        <div class="rf-events__body">
          <div class="rf-events__badges">${badges.join('')}</div>
          <h3 class="rf-events__name" id="${esc(id)}-t">${esc(ev.name)}</h3>
          ${when}
          <p class="rf-events__where">${country}${where ? `<span class="rf-events__place">${icon('pin')}<span>${where}</span></span>` : ''}</p>
          ${ev.note || source ? `<p class="rf-events__note">${esc(ev.note || '')}${ev.note && source ? ' ' : ''}${source}</p>` : ''}
          ${actions.length ? `<div class="rf-events__actions">${actions.join('')}</div>` : ''}
        </div>
      </article>`;
    }

    renderCalendar() {
      const { list, dated, tba, programmes, stale } = this.current;
      if (!list.length) return this.emptyState();
      const undated = tba.length + programmes.length + stale.length;
      const key = this.calMonth;
      const y = Number(key.slice(0, 4)), m = Number(key.slice(5, 7)) - 1;
      const first = `${key}-01`;
      const daysIn = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
      const last = `${key}-${pad(daysIn)}`;
      const inMonth = dated
        .filter((e) => e.start <= last && e.end >= first)
        .sort((a, b) =>
          a.start === b.start ? a.soccerRank - b.soccerRank || LEVELS[a.level].rank - LEVELS[b.level].rank : a.start < b.start ? -1 : 1,
        );
      const shift = (k, n) => {
        const d = new Date(Date.UTC(Number(k.slice(0, 4)), Number(k.slice(5, 7)) - 1 + n, 1));
        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
      };
      const months = [...new Set(dated.flatMap((e) => [monthKey(e.start), monthKey(e.end)]))].sort();
      const prevWith = [...months].reverse().find((k) => k < key);
      const nextWith = months.find((k) => k > key);

      // Grid cells
      const lead = (new Date(Date.UTC(y, m, 1)).getUTCDay() + 6) % 7;
      const cells = [];
      for (let i = 0; i < lead; i++) cells.push('<td class="rf-events__cell is-out" aria-hidden="true"></td>');
      for (let d = 1; d <= daysIn; d++) {
        const day = `${key}-${pad(d)}`;
        const evs = inMonth.filter((e) => e.start <= day && e.end >= day);
        const isToday = day === this.today;
        const label = `${d} ${MONTHS[m]}${isToday ? `, ${T('todayAria')}` : ''}${evs.length ? `: ${evs.map((e) => e.name).join('; ')}` : ''}`;
        const pills = evs
          .slice(0, 3)
          .map((e) => {
            const pos = e.start === e.end ? 'single' : day === e.start ? 'start' : day === e.end ? 'end' : 'mid';
            const weekStart = (d - 1 + lead) % 7 === 0;
            let txt = '&nbsp;';
            if (pos === 'single' || day === e.start || weekStart) {
              // Label runs across the event's remaining days in this week row
              const toEnd = Math.round((parse(e.end < last ? e.end : last) - parse(day)) / 864e5) + 1;
              const span = Math.max(1, Math.min(toEnd, 7 - ((d - 1 + lead) % 7)));
              txt = `<span class="rf-events__pilltxt" style="--span:${span}">${esc(e.name)}</span>`;
            }
            return `<span class="rf-events__pill rf-events__pill--${esc(e.level)}${e.soccer ? ' is-soccer' : ''}${e.attending ? ' is-attending' : ''} is-${pos}" title="${esc(e.name)}">${txt}</span>`;
          })
          .join('');
        const more = evs.length > 3 ? `<span class="rf-events__more">${esc(T('more', { n: evs.length - 3 }))}</span>` : '';
        const dots = evs.length ? `<span class="rf-events__dots" aria-hidden="true">${evs.slice(0, 3).map((e) => `<i class="${e.soccer ? 'is-soccer' : ''}"></i>`).join('')}</span>` : '';
        const inner = `<span class="rf-events__daynum">${d}${isToday && !evs.length ? `<span class="rf-visually-hidden">, ${esc(T('todayAria'))}</span>` : ''}</span>${dots}<span class="rf-events__pills">${pills}${more}</span>`;
        cells.push(
          `<td class="rf-events__cell${evs.length ? ' has-events' : ''}${isToday ? ' is-today' : ''}${day < this.today ? ' is-before' : ''}">${
            evs.length
              ? `<button type="button" class="rf-events__daybtn" data-rf-day="${esc(evs[0].handle)}" aria-label="${esc(label)}">${inner}</button>`
              : `<span class="rf-events__daybtn">${inner}</span>`
          }</td>`,
        );
      }
      while (cells.length % 7) cells.push('<td class="rf-events__cell is-out" aria-hidden="true"></td>');
      const rows = [];
      for (let i = 0; i < cells.length; i += 7) rows.push(`<tr>${cells.slice(i, i + 7).join('')}</tr>`);

      const navBtn = (dir, target, text) =>
        `<button type="button" class="rf-events__navbtn" data-rf-month="${target}" data-rf-month-nav="${dir}" aria-label="${esc(text)}: ${esc(monthLabel(target))}">${icon(dir === 'prev' ? 'chevronL' : 'chevronR')}</button>`;

      const listHtml = inMonth.length
        ? `<div class="rf-events__cards">${inMonth.map((e) => this.card(e, 'cal')).join('')}</div>`
        : `<div class="rf-events__empty rf-events__empty--month">
            <p>${esc(T('calEmptyMonth', { month: monthLabel(key) }))}</p>
            <div class="rf-events__jump">
              ${prevWith ? `<button type="button" class="rf-btn rf-btn--ghost rf-btn--sm" data-rf-month="${prevWith}">${icon('chevronL')}${esc(monthLabel(prevWith))}</button>` : ''}
              ${nextWith ? `<button type="button" class="rf-btn rf-btn--ghost rf-btn--sm" data-rf-month="${nextWith}">${esc(monthLabel(nextWith))}${icon('chevronR')}</button>` : ''}
            </div>
          </div>`;

      return `<div class="rf-events__cal">
        <div class="rf-events__calhead">
          ${navBtn('prev', shift(key, -1), T('prev'))}
          <h2 class="rf-events__callabel" data-rf-cal-label tabindex="-1" aria-live="polite">${esc(monthLabel(key))}</h2>
          ${navBtn('next', shift(key, 1), T('next'))}
          ${monthKey(this.today) !== key ? `<button type="button" class="rf-events__todaybtn" data-rf-month="${monthKey(this.today)}">${esc(T('today'))}</button>` : ''}
        </div>
        <table class="rf-events__grid">
          <caption class="rf-visually-hidden">${esc(T('calCaption', { month: monthLabel(key) }))}</caption>
          <thead><tr>${WEEKDAYS.map((w, i) => `<th scope="col" abbr="${WEEKDAYS_LONG[i]}">${w}</th>`).join('')}</tr></thead>
          <tbody>${rows.join('')}</tbody>
        </table>
        <div class="rf-events__legendrow" aria-hidden="true">
          <span><i class="rf-events__key rf-events__key--soccer"></i>${esc(T('soccer'))}</span>
          <span><i class="rf-events__key"></i>${esc(T(this.state.soccer ? 'calTbc' : 'calOther'))}</span>
        </div>
        <section class="rf-events__calList" aria-label="${esc(T('calEventsIn', { month: monthLabel(key) }))}">
          <h3 class="rf-events__month-title">${esc(monthLabel(key))} <span>${esc(TN('countEvent', inMonth.length))}</span></h3>
          ${listHtml}
        </section>
        ${
          undated
            ? `<p class="rf-events__caltba"><span>${esc(TN('calUndated', undated))}</span>
              <button type="button" class="rf-events__hint" data-rf-switch-list>${esc(T('calShowList'))}</button></p>`
            : ''
        }
      </div>`;
    }

    // Deep link: /pages/events#rf-event-<handle> — make sure the event is visible, then focus it.
    focusHash() {
      const m = /^#rf-event-([a-z0-9-]+)$/.exec(window.location.hash);
      if (!m) return;
      const ev = this.events.find((e) => e.handle === m[1]);
      if (!ev) return;
      // Keep the visitor's filters (country, region, search, levels, soccer, attending); switch to the list if needed,
      // and if the filters exclude the event, pin just that event at the top instead of widening them.
      if (this.state.view !== 'list') this.state.view = 'list';
      this.pinned = this.matches(ev) ? '' : ev.handle;
      this.syncControls();
      this.writeUrl();
      this.render();
      this.revealEvent(ev);
    }
  }

  // "How this calendar works" (phones): shows/hides the intro and the attendance note.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rf-how-toggle]');
    if (!btn) return;
    const panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', String(open));
    panel.classList.toggle('is-open', open);
  });

  // "list view" link inside the calendar
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-rf-switch-list]');
    if (!btn) return;
    const host = btn.closest('rf-events');
    const radio = host && host.querySelector('[data-rf-view][value="list"]');
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      radio.focus();
    }
  });

  customElements.define('rf-events', RfEvents);
})();
